// Stap 9: verificatie + completion. PRD-06 §4 US-ONB-09.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import type { OnboardingProgress } from "../OnboardingWizard";

interface Props {
  progress: OnboardingProgress;
  onBack: () => void;
  onComplete: () => void;
}

export function VerifyStep({ progress, onBack, onComplete }: Props) {
  const { t } = useTranslation();
  const [confirmed, setConfirmed] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () => api.post<OnboardingProgress>("/v1/onboarding/complete"),
    onSuccess: () => onComplete(),
    onError: (e) => setErrorKey(e instanceof ApiError ? e.i18nKey : "errors.internal"),
  });

  const summary: Array<{ k: string; default: string; v: string; ok: boolean }> = [
    {
      k: "onboarding.verify.consent",
      default: "Toestemming",
      v: progress.gdprConsentAt
        ? new Date(progress.gdprConsentAt).toLocaleDateString("nl-NL")
        : "–",
      ok: !!progress.gdprConsentAt,
    },
    {
      k: "onboarding.verify.period",
      default: "Analyseperiode",
      v: `${progress.analysisPeriodYears} ${t("onboarding.welcome.yearSuffix", "jaar")}`,
      ok: true,
    },
    {
      k: "onboarding.verify.personal",
      default: "Persoonlijke gegevens",
      v: progress.personalDataComplete ? t("onboarding.verify.done", "Voltooid") : t("onboarding.verify.missing", "Ontbreekt"),
      ok: progress.personalDataComplete,
    },
    {
      k: "onboarding.verify.bank",
      default: "Bankrekeningen",
      v: progress.bankAccountsComplete ? t("onboarding.verify.done", "Voltooid") : t("onboarding.verify.skipped", "Overgeslagen"),
      ok: true,
    },
    {
      k: "onboarding.verify.email",
      default: "E-mail",
      v: progress.emailConnected ? t("onboarding.verify.connected", "Gekoppeld") : t("onboarding.verify.skipped", "Overgeslagen"),
      ok: true,
    },
  ];

  return (
    <article className="bg-white rounded-2xl border border-neutral-200 p-6 sm:p-8">
      <h2 className="text-2xl font-semibold mb-2">
        {t("onboarding.verify.title", "Bevestig je gegevens")}
      </h2>
      <p className="text-neutral-700 mb-6">
        {t(
          "onboarding.verify.subtitle",
          "Bijna klaar. Bekijk je gegevens en bevestig dat alles klopt."
        )}
      </p>

      <dl className="space-y-2 mb-6">
        {summary.map((row) => (
          <div
            key={row.k}
            className="flex items-center justify-between px-3 py-2 bg-neutral-50 rounded-lg"
          >
            <dt className="text-sm text-neutral-700">{t(row.k, row.default)}</dt>
            <dd className={`text-sm font-medium ${row.ok ? "text-neutral-900" : "text-red-700"}`}>
              {row.v}
            </dd>
          </div>
        ))}
      </dl>

      <label className="flex items-start gap-2 mb-6 cursor-pointer">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          className="mt-1"
          required
        />
        <span className="text-sm text-neutral-700">
          {t(
            "onboarding.verify.confirm",
            "Ik bevestig dat de bovenstaande gegevens naar mijn beste weten correct zijn."
          )}
        </span>
      </label>

      {errorKey && (
        <div role="alert" className="mb-4 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
          {t(errorKey)}
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className="px-4 py-2.5 text-neutral-700 hover:bg-neutral-100 rounded-lg"
        >
          ← {t("back", "Terug")}
        </button>
        <button
          type="button"
          onClick={() => submit.mutate()}
          disabled={!confirmed || submit.isPending}
          className="flex-1 px-4 py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium rounded-lg"
        >
          {submit.isPending
            ? t("loading", "Laden…")
            : t("onboarding.verify.complete", "Voltooien en naar dashboard")}
        </button>
      </div>
    </article>
  );
}
